import investorService from '@/services/investorService';
import managerService from '@/services/managerService';

/**
 * Preference Service
 * Loads and caches investor preferences at app startup
 * Similar pattern to lookupService.js
 */
class PreferenceService {
  constructor() {
    this.cache = new Map();
    this.isInitialized = false;
    this.initializationPromise = null;
    this.currentUserId = null;
    this.currentUserRole = null;
    /** @type {Set<() => void>} */
    this._listeners = new Set();
  }

  _notifyListeners() {
    this._listeners.forEach((cb) => {
      try {
        cb();
      } catch (e) {
        console.error('PreferenceService listener error:', e);
      }
    });
  }

  /** Subscribe to cache updates (login load, updateCachedPreferences, clear). Returns unsubscribe. */
  subscribe(listener) {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  // Initialize preferences for authenticated user
  async initializeUserPreferences(userId, userRole) {
    // Only load for investors and managers
    if (userRole !== 'investor' && userRole !== 'manager') {
      console.log(`👤 User role '${userRole}' does not use preferences, skipping initialization`);
      this.isInitialized = true;
      this._notifyListeners();
      return {};
    }

    // If already initialized for this user, return cached data
    if (this.isInitialized && this.currentUserId === userId) {
      return this.cache.get('preferences') || {};
    }

    // If initialization is in progress for this user, wait for it
    if (this.initializationPromise && this.currentUserId === userId) {
      return this.initializationPromise;
    }

    this.currentUserId = userId;
    this.currentUserRole = userRole;
    this.initializationPromise = this._performPreferencesInitialization(userId);
    
    try {
      const result = await this.initializationPromise;
      return result;
    } finally {
      this.initializationPromise = null;
    }
  }

  async _performPreferencesInitialization(userId) {
    try {
      console.log(`🔄 Initializing ${this.currentUserRole} preferences...`);
      const startTime = Date.now();

      // Stable object for getPreferences() / useSyncExternalStore before the network returns
      if (!this.cache.has('preferences')) {
        this.cache.set('preferences', this._formatPreferences({}));
        this._notifyListeners();
      }

      // Load preferences from appropriate service
      let response;
      let preferencesData;
      
      if (this.currentUserRole === 'investor') {
        response = await investorService.getPreferences(userId);
        preferencesData = response?.data?.investor_preference || response?.investor_preference || {};
      } else if (this.currentUserRole === 'manager') {
        response = await managerService.getPreferences(userId);
        preferencesData = response?.data?.manager_preference || response?.manager_preference || {};
      }
      
      console.log('📦 Received preferences response:', response);
      
      // Format preferences for consistent use
      const formattedPreferences = this._formatPreferences(preferencesData);
      
      // Cache the formatted preferences
      this.cache.set('preferences', formattedPreferences);
      this.isInitialized = true;
      
      const loadTime = Date.now() - startTime;
      console.log(`✅ ${this.currentUserRole} preferences initialization complete in ${loadTime}ms`);
      console.log('📊 Loaded preferences:', formattedPreferences);

      this._notifyListeners();
      return formattedPreferences;
    } catch (error) {
      console.error(`❌ Failed to initialize ${this.currentUserRole} preferences:`, error);
      // Don't mark as initialized on error, but cache empty preferences to prevent repeated failures
      this.cache.set('preferences', {});
      this.isInitialized = true; // Mark as initialized to prevent infinite retries
      this._notifyListeners();
      return {};
    }
  }

  _formatPreferences(rawPreferences) {
    if (!rawPreferences || typeof rawPreferences !== 'object') {
      return this._getDefaultPreferences();
    }

    // Handle manager preferences differently from investor preferences
    if (this.currentUserRole === 'manager') {
      return this._formatManagerPreferences(rawPreferences);
    } else if (this.currentUserRole === 'investor') {
      return this._formatInvestorPreferences(rawPreferences);
    }

    return this._getDefaultPreferences();
  }

  _formatManagerPreferences(rawPreferences) {
    return {
      show_dashboard_metrics: rawPreferences.show_dashboard_metrics !== undefined 
        ? Boolean(rawPreferences.show_dashboard_metrics) 
        : true, // Default to true
      email_notifications_enabled: rawPreferences.email_notifications_enabled !== undefined
        ? Boolean(rawPreferences.email_notifications_enabled)
        : true,
      notification_email: rawPreferences.notification_email || '',
      notification_frequency: rawPreferences.notification_frequency || 'realtime',
      suppress_when_online: rawPreferences.suppress_when_online !== undefined
        ? Boolean(rawPreferences.suppress_when_online)
        : true,
      last_updated: rawPreferences.last_updated || new Date().toISOString(),
      updated_by_admin: rawPreferences.updated_by_admin || null
    };
  }

  _formatInvestorPreferences(rawPreferences) {
    /** API may send arrays, comma-separated strings, or a single code — same as Explore mandate parsing. */
    const safeArray = (value) => {
      if (value == null) return [];
      if (Array.isArray(value)) return value.map(String).filter(Boolean);
      if (typeof value === 'string') {
        return value.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
      }
      return [String(value)].filter(Boolean);
    };

    const minRetRaw = rawPreferences.min_return ?? rawPreferences.minReturn;
    const min_return =
      minRetRaw != null && minRetRaw !== '' && Number.isFinite(Number(minRetRaw))
        ? Number(minRetRaw)
        : null;

    return {
      asset_types: safeArray(rawPreferences.asset_types),
      strategy_focus: safeArray(rawPreferences.strategy_focus),
      regions: safeArray(rawPreferences.regions),
      sectors: safeArray(rawPreferences.sectors),
      min_return,
      view_mode: rawPreferences.view_mode || 'light',
      default_explore_view: rawPreferences.default_explore_view || 'grid',
      default_grid_layout: rawPreferences.default_grid_layout === 'table' ? 'table' : 'tiles',

      email_notifications_enabled: rawPreferences.email_notifications_enabled !== undefined
        ? Boolean(rawPreferences.email_notifications_enabled)
        : true,
      notification_email: rawPreferences.notification_email || '',
      notification_frequency: rawPreferences.notification_frequency || 'realtime',
      suppress_when_online: rawPreferences.suppress_when_online !== undefined
        ? Boolean(rawPreferences.suppress_when_online)
        : true,
      push_notifications_enabled: rawPreferences.push_notifications_enabled !== undefined
        ? Boolean(rawPreferences.push_notifications_enabled)
        : false
    };
  }

  _getDefaultPreferences() {
    if (this.currentUserRole === 'manager') {
      return {
        show_dashboard_metrics: true,
        email_notifications_enabled: true,
        notification_email: '',
        notification_frequency: 'realtime',
        suppress_when_online: true,
        last_updated: new Date().toISOString(),
        updated_by_admin: null
      };
    } else if (this.currentUserRole === 'investor') {
      return {
        asset_types: [],
        strategy_focus: [],
        regions: [],
        sectors: [],
        min_return: null,
        view_mode: 'light',
        default_explore_view: 'grid',
        default_grid_layout: 'tiles',
        email_notifications_enabled: true,
        notification_email: '',
        notification_frequency: 'realtime',
        suppress_when_online: true,
        push_notifications_enabled: false
      };
    }
    
    return {};
  }

  // Get current preferences
  getPreferences() {
    if (this.cache.has('preferences')) {
      return this.cache.get('preferences');
    }
    const fallback = this._getDefaultPreferences();
    this.cache.set('preferences', fallback);
    return fallback;
  }

  // Convert preferences to ExploreOpportunities filter format
  getDefaultFilters() {
    if (!this.isInitialized) {
      console.warn('⚠️ Preferences not initialized, using minimal fallback filters');
      return {
        search: '',
        searchType: 'firm',
        dealSizeMin: 0,
        dealSizeMax: 1000,
        sectors: [],
        instrumentTypes: [],
        instrumentSubTypes: [],
        assetTypes: [],
        strategyFocus: [],
        regions: [],
        statuses: ['Open'],
        locations: [],
        dealTypes: [],
        minReturnMin: 0,
        daysRemainingMin: 0,
        daysRemainingMax: 365
      };
    }

    const preferences = this.cache.get('preferences') || {};

    return {
      search: '',
      searchType: 'firm',
      dealSizeMin: 0,
      dealSizeMax: 1000,
      sectors: preferences.sectors || [],
      instrumentTypes: [],
      instrumentSubTypes: [],
      assetTypes: preferences.asset_types || [],
      strategyFocus: preferences.strategy_focus || [],
      regions: preferences.regions || [],
      statuses: ['Open'],
      locations: [],
      dealTypes: [],
      minReturnMin: 0,
      daysRemainingMin: 0,
      daysRemainingMax: 365
    };
  }

  // Get view mode for theme integration
  getViewMode() {
    const preferences = this.getPreferences();
    return preferences.view_mode || 'light';
  }

  // Get manager-specific preference values
  getManagerPreference(key, defaultValue = null) {
    if (this.currentUserRole !== 'manager') {
      return defaultValue;
    }
    
    const preferences = this.getPreferences();
    return preferences[key] !== undefined ? preferences[key] : defaultValue;
  }

  // Check if dashboard metrics should be shown (for managers)
  shouldShowDashboardMetrics() {
    return this.getManagerPreference('show_dashboard_metrics', true);
  }

  shouldShowOpportunityCardMetrics() {
    return this.getManagerPreference('show_opportunity_card_metrics', false);
  }

  // Check if preferences are ready
  isReady() {
    return this.isInitialized;
  }

  // Wait for initialization to complete
  async waitForInitialization() {
    if (this.isInitialized) return true;
    
    if (this.initializationPromise) {
      try {
        await this.initializationPromise;
        return true;
      } catch (error) {
        console.error('Failed to wait for preferences initialization:', error);
        return false;
      }
    }
    
    // If not initialized and no promise, we haven't started initialization
    return false;
  }

  // Clear cache (useful for logout)
  clearCache() {
    this.cache.clear();
    this.isInitialized = false;
    this.currentUserId = null;
    this.currentUserRole = null;
    this.initializationPromise = null;
    console.log('🧹 Preferences cache cleared');
    this._notifyListeners();
  }

  // Update cached preferences (when user saves new preferences)
  updateCachedPreferences(newPreferences) {
    const formattedPreferences = this._formatPreferences(newPreferences);
    this.cache.set('preferences', formattedPreferences);
    console.log('🔄 Cached preferences updated:', formattedPreferences);
    this._notifyListeners();
    return formattedPreferences;
  }
}

// Create singleton instance
const preferenceService = new PreferenceService();

// Initialize preferences when called (similar to lookupService pattern)
export const initializeUserPreferences = (userId, userRole) => {
  return preferenceService.initializeUserPreferences(userId, userRole);
};

// Utility function to ensure preferences are ready before using
export const withPreferences = async (callback) => {
  await preferenceService.waitForInitialization();
  return callback(preferenceService);
};

export default preferenceService; 